import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, ChangeDetectionStrategy } from "@angular/core";
import {
  DateRange,
  DefaultMatCalendarRangeStrategy,
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatRangeDateSelectionModel,
} from "@angular/material/datepicker";

@Component({
  selector: "app-date-range-picker",
  templateUrl: "./date-range-picker.component.html",
  styleUrls: ["./date-range-picker.component.scss"],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    DefaultMatCalendarRangeStrategy,
    MatRangeDateSelectionModel,
    { provide: MAT_DATE_RANGE_SELECTION_STRATEGY, useClass: DefaultMatCalendarRangeStrategy },
  ],
})
export class DateRangePickerComponent implements OnInit {
  @Input() isR4 = false;
  @Output() dateRangeApplied = new EventEmitter<{ start: Date; end: Date }>();
  @Output() dateRangeCleared = new EventEmitter<void>();
  @ViewChild('startInput') private startInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('endInput')   private endInputRef?: ElementRef<HTMLInputElement>;

  selectedRange: DateRange<Date> = new DateRange<Date>(null, null);
  drActivePreset: number | null = null;
  drApplied = false;
  readonly maxDate = new Date();
  readonly minDate = new Date(1930, 0, 1);
  startInputVal = '';
  endInputVal = '';
  startInputError = '';
  endInputError = '';

  private readonly monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  constructor(
    private readonly selectionModel: MatRangeDateSelectionModel<Date>,
    private readonly selectionStrategy: DefaultMatCalendarRangeStrategy<Date>,
  ) {}

  ngOnInit(): void {
    this.selectedRange = this.selectionModel.selection;
    this.syncInputs();
  }

  get drRangeStart(): Date | null {
    return this.selectedRange.start ?? null;
  }

  get drRangeEnd(): Date | null {
    return this.selectedRange.end ?? null;
  }

  get drHint(): string {
    if (!this.drRangeStart) return "Select a start date";
    if (!this.drRangeEnd) return "Now select an end date";
    return "Range selected";
  }

  onRangeChange(date: Date): void {
    const newSelection = this.selectionStrategy.selectionFinished(
      date,
      this.selectionModel.selection,
    );
    this.selectionModel.updateSelection(newSelection, this);
    this.selectedRange = newSelection;
    this.drActivePreset = null;
    this.drApplied = false;
    this.syncInputs();
  }

  drFmtShort(d: Date | null): string {
    if (!d) return "—";
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${m}/${day}/${d.getFullYear()}`;
  }

  drFmtFull(d: Date | null): string {
    if (!d) return "—";
    return `${this.monthNames[d.getMonth()].slice(0, 3)} ${d.getDate()}, ${d.getFullYear()}`;
  }

  drPreset(days: number): void {
    this.drActivePreset = days;
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    const newSelection = new DateRange<Date>(start, end);
    this.selectionModel.updateSelection(newSelection, this);
    this.selectedRange = newSelection;
    this.drApplied = false;
    this.syncInputs();
  }

  drApply(): void {
    if (!this.drRangeStart || !this.drRangeEnd) return;
    this.drApplied = true;
    this.dateRangeApplied.emit({ start: this.drRangeStart, end: this.drRangeEnd });
  }

  drClear(): void {
    const empty = new DateRange<Date>(null, null);
    this.selectionModel.updateSelection(empty, this);
    this.selectedRange = empty;
    this.drActivePreset = null;
    this.drApplied = false;
    this.syncInputs();
    this.dateRangeCleared.emit();
  }

  onStartInputCommit(val: string): void {
    const error = this.getInputError(val, this.drRangeEnd, true);
    if (error) {
      this.startInputError = error;
      return;
    }
    this.startInputError = '';
    if (!val.trim()) {
      this.startInputVal = this.dateToInput(this.drRangeStart);
      return;
    }
    const parsed = this.parseInputDate(val)!;
    const newRange = new DateRange<Date>(parsed, this.drRangeEnd);
    this.selectionModel.updateSelection(newRange, this);
    this.selectedRange = newRange;
    this.drActivePreset = null;
    this.drApplied = false;
    this.startInputVal = this.dateToInput(parsed);
  }

  onEndInputCommit(val: string): void {
    const error = this.getInputError(val, this.drRangeStart, false);
    if (error) {
      this.endInputError = error;
      return;
    }
    this.endInputError = '';
    if (!val.trim()) {
      this.endInputVal = this.dateToInput(this.drRangeEnd);
      return;
    }
    const parsed = this.parseInputDate(val)!;
    const newRange = new DateRange<Date>(this.drRangeStart, parsed);
    this.selectionModel.updateSelection(newRange, this);
    this.selectedRange = newRange;
    this.drActivePreset = null;
    this.drApplied = false;
    this.endInputVal = this.dateToInput(parsed);
  }

  private syncInputs(): void {
    this.startInputVal = this.dateToInput(this.drRangeStart);
    this.endInputVal = this.dateToInput(this.drRangeEnd);
    this.startInputError = '';
    this.endInputError = '';
    // [value] binding only re-applies when the expression changes; force DOM sync
    // so any stale typed value the user left in the input is always overwritten.
    if (this.startInputRef) this.startInputRef.nativeElement.value = this.startInputVal;
    if (this.endInputRef)   this.endInputRef.nativeElement.value   = this.endInputVal;
  }

  private getInputError(val: string, otherDate: Date | null, isStart: boolean): string {
    if (!val.trim()) return '';
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(val.trim())) return 'Use MM/DD/YYYY format';
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(val.trim())!;
    const month = parseInt(match[1], 10);
    const day   = parseInt(match[2], 10);
    const year  = parseInt(match[3], 10);
    if (month < 1 || month > 12) return 'Invalid date';
    const d = new Date(year, month - 1, day);
    if (d.getMonth() !== month - 1 || d.getDate() !== day) return 'Invalid date';
    if (d > this.maxDate) return 'Date cannot be in the future';
    if (d < this.minDate) return 'Date cannot be before 01/01/1930';
    if (isStart  && otherDate && d > otherDate) return 'Must be before end date';
    if (!isStart && otherDate && d < otherDate) return 'Must be after start date';
    return '';
  }

  private dateToInput(d: Date | null): string {
    if (!d) return '';
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${m}/${day}/${d.getFullYear()}`;
  }

  formatDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let digits = input.value.replace(/\D/g, '').slice(0, 8);
    if (digits.length > 4) {
      digits = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    } else if (digits.length > 2) {
      digits = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }
    input.value = digits;
  }

  private parseInputDate(val: string): Date | null {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(val.trim());
    if (!match) return null;
    const month = parseInt(match[1], 10);
    const day = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);
    if (month < 1 || month > 12) return null;
    const d = new Date(year, month - 1, day);
    if (d.getMonth() !== month - 1 || d.getDate() !== day) return null;
    if (d > this.maxDate) return null;
    if (d < this.minDate) return null;
    return d;
  }
}
